// Event emitter for application-wide events
import { LandingPageProposal } from '@components/genAI/models';
import { AssignmentSchedulingJobStatus, MediaitemFileRole } from '@models';
import { ImprvCoinsWarningReason } from '@models/imprvCoins';

// Enum of all possible event types
export enum EventType {
  GOAL_SUBSCRIPTION_CHANGED = 'GOAL_SUBSCRIPTION_CHANGED',
  NEW_MESSAGE_RECEIVED = 'NEW_MESSAGE_RECEIVED',
  MESSAGE_UPDATED = 'MESSAGE_UPDATED',
  CHATBOT_REPLY_STARTED = 'CHATBOT_REPLY_STARTED',
  CHATBOT_REPLY_FINISHED = 'CHATBOT_REPLY_FINISHED',
  NEW_MESSAGE_REPLY_ESTIMATION = 'NEW_MESSAGE_REPLY_ESTIMATION',
  NEW_CHATBOT_TEST_REQUEST_RESPONSE = 'NEW_CHATBOT_TEST_REQUEST_RESPONSE',
  NEW_MEDIAITEM_FILE_IS_READY = 'NEW_MEDIAITEM_FILE_IS_READY',
  SCHEDULE_ASSIGNMENTS_JOB_STATUS_UPDATED = 'SCHEDULE_ASSIGNMENTS_JOB_STATUS_UPDATED',
  GOAL_SUBSCRIPTION_STRATEGY_END_REACHED = 'GOAL_SUBSCRIPTION_STRATEGY_END_REACHED',
  TASK_PROGRESS_UPDATED = 'TASK_PROGRESS_UPDATED',
  GOAL_PROGRESS_UPDATED = 'GOAL_PROGRESS_UPDATED',
  GENAI_GENERATOR_FINISHED = 'GENAI_GENERATOR_FINISHED',
  OPEN_TASK_FROM_LIST = 'OPEN_TASK_FROM_LIST',
  BLOCK_OUTPUT_EVALUATED = 'BLOCK_OUTPUT_EVALUATED',
  BLOCK_OUTPUT_AI_USAGE_RECORDED = 'BLOCK_OUTPUT_AI_USAGE_RECORDED',
  LOGOUT_REQUESTED = 'LOGOUT_REQUESTED',
  AUTH_SESSION_INVALID = 'AUTH_SESSION_INVALID',
  GENAI_LANDING_PAGE_PROPOSAL = 'GENAI_LANDING_PAGE_PROPOSAL',
  RECOMMENDATION_RESOLVED = 'RECOMMENDATION_RESOLVED',
  IMPRV_COINS_WARNING_DISMISSED = 'IMPRV_COINS_WARNING_DISMISSED'
}

// Type definitions for all event data structures
export interface EventData {
  event: EventType;
  data:
    | GoalSubscriptionChangedData
    | NewMessageReceivedData
    | MessageUpdatedData
    | ChatbotReplyStartedData
    | ChatbotReplyFinishedData
    | NewMessageReplyEstimationData
    | NewChatbotTestRequestResponseData
    | NewMediaitemFileIsReadyData
    | ScheduleAssignmentsJobStatusUpdatedData
    | GoalSubscriptionStrategyEndReachedData
    | TaskProgressUpdatedData
    | GoalProgressUpdatedData
    | GenAIGeneratorFinishedData
    | OpenTaskFromListData
    | BlockOutputEvaluatedData
    | BlockOutputAiUsageRecordedData
    | GenAILandingPageProposalData
    | RecommendationResolvedData
    | ImprvCoinsWarningDismissedData;
}

export interface GoalSubscriptionChangedData {
  goalEntityId: string;
  goalSubscriptionId: number;
  status: string;
  previousStatus: string;
}

export interface NewMessageReceivedData {
  conversationId: number;
  messageCreatedAt: string;
}

export interface MessageUpdatedData {
  conversationId: number;
  messageId: number;
}

export enum ChatbotReplyOutcome {
  REPLIED = 'replied',
  RECOMMENDED = 'recommended',
  IRRELEVANT = 'irrelevant',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

// The subset of outcomes that surface an in-chat notice to the improver.
export type ChatbotReplyNoticeOutcome =
  | ChatbotReplyOutcome.FAILED
  | ChatbotReplyOutcome.RECOMMENDED
  | ChatbotReplyOutcome.IRRELEVANT;

export interface ChatbotReplyStartedData {
  conversationId: number;
  inReplyToMessageId: number;
}

export interface ChatbotReplyFinishedData {
  conversationId: number;
  inReplyToMessageId: number | null;
  outcome: ChatbotReplyOutcome;
}

export interface NewMessageReplyEstimationData {
  conversationId: number;
  messageId: number;
}

export interface NewChatbotTestRequestResponseData {
  chatbotEntityId: string;
  chatbotTestRequestId: number;
}

export interface NewMediaitemFileIsReadyData {
  mediaitemId: number;
  mediaitemFileRole: MediaitemFileRole;
}

export interface ScheduleAssignmentsJobStatusUpdatedData {
  status: AssignmentSchedulingJobStatus;
  timestamp: number;
}

export interface GoalSubscriptionStrategyEndReachedData {
  goalSubscriptionId: number;
}

export interface TaskProgressUpdatedData {
  taskEntityId: string;
}

export interface GoalProgressUpdatedData {
  goalEntityId: string;
}

export interface GenAIGeneratorFinishedData {
  type: string;
  block_entity_id?: string | null;
  task_entity_id?: string | null;
  skill_graph_entity_id?: string | null;
}

export interface OpenTaskFromListData {
  taskEntityId: string;
  pathEntityId: string;
}

export interface BlockOutputEvaluatedData {
  blockEntityId: string;
  blockOutputId: string;
  itemCid?: string | null;
  attemptId?: string | null;
}

export interface BlockOutputAiUsageRecordedData {
  blockEntityId: string;
  nodeEntityId: string;
  goalSubscriptionId: number;
}

export interface GenAILandingPageProposalData {
  page: LandingPageProposal;
  done: boolean;
}

export interface RecommendationResolvedData {
  conversationId: number;
}

export interface ImprvCoinsWarningDismissedData {
  reason: ImprvCoinsWarningReason;
}

// Event listener type
export type EventListener<T> = (data: T) => void;

// Event emitter class
export class EventEmitter {
  private listeners: Map<EventType, EventListener<any>[]> = new Map();

  on<T>(event: EventType, listener: EventListener<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(listener);

    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(event);
      if (!eventListeners) return;

      const index = eventListeners.indexOf(listener);
      if (index !== -1) {
        eventListeners.splice(index, 1);
      }
    };
  }

  emit<T>(event: EventType, data: T): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      for (const listener of eventListeners) {
        listener(data);
      }
    }
  }
}

// Single application-wide bus. Import this directly to publish/subscribe.
// Subscribers must keep the returned unsubscribe and call it in cleanup.
export const eventEmitter = new EventEmitter();
